import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './apiClient';

const subscriberKeys = {
  all: ['subscribers'],
  lists: () => [...subscriberKeys.all, 'list'],
  details: () => [...subscriberKeys.all, 'detail'],
  detail: (id) => [...subscriberKeys.details(), id],
  coupons: () => [...subscriberKeys.all, 'coupons'],
  couponsFilter: (filters) => [...subscriberKeys.coupons(), 'filter', filters],
  allCoupons: (filters) => [...subscriberKeys.coupons(), 'all', filters],
  mailing: () => [...subscriberKeys.all, 'mailing'],
};

export const useSubscribers = () => {
  return useQuery({
    queryKey: subscriberKeys.lists(),
    queryFn: async () => {
      const response = await apiClient.get('subscribers');
      return response.data;
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
};

export const useSubscriber = (id, options = {}) => {
  return useQuery({
    queryKey: subscriberKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.get(`subscribers/${id}`);
      return response.data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

export const useCreateSubscriber = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (subscriberData) => {
      const response = await apiClient.post('subscribers', subscriberData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subscriberKeys.lists() });
    },
    onError: (error) => {
      console.error('Error creating subscriber:', error);
    },
  });
};

export const useUpdateSubscriber = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (subscriberData) => {
      const response = await apiClient.put(`subscribers/${subscriberData.uuid}`, subscriberData);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(subscriberKeys.detail(data.uuid), data);
      queryClient.invalidateQueries({ queryKey: subscriberKeys.lists() });
    },
    onError: (error) => {
      console.error('Error updating subscriber:', error);
    },
  });
};

export const useDeleteSubscriber = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const response = await apiClient.delete(`subscribers/${id}`);
      return response.data;
    },
    onSuccess: (data, deletedId) => {
      queryClient.invalidateQueries({ queryKey: subscriberKeys.lists() });
      queryClient.removeQueries({ queryKey: subscriberKeys.detail(deletedId) });
    },
    onError: (error) => {
      console.error('Error deleting subscriber:', error);
    },
  });
};

export const useCouponsForMails = (filters = {}, options = {}) => {
  return useQuery({
    queryKey: subscriberKeys.couponsFilter(filters),
    queryFn: async () => {
      const cleanFilters = { ...filters };

      // Limpiar filtros vacíos pero mantener 'order'
      Object.keys(cleanFilters).forEach((key) => {
        if (
          cleanFilters[key] === '' ||
          cleanFilters[key] === null ||
          cleanFilters[key] === undefined
        ) {
          delete cleanFilters[key];
        }
      });

      // Manejar el filtro de grupo "todos"
      if (cleanFilters.group === 'todos' || cleanFilters.group === 'all') {
        delete cleanFilters.group;
      }

      const response = await apiClient.get('subscribers/coupons/filter', {
        params: cleanFilters,
      });
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    ...options,
  });
};

export const useAllCouponsForMails = (filters = {}, options = {}) => {
  return useQuery({
    queryKey: subscriberKeys.allCoupons(filters),
    queryFn: async () => {
      const cleanFilters = { ...filters };

      delete cleanFilters.page;
      delete cleanFilters.per_page;

      Object.keys(cleanFilters).forEach((key) => {
        if (
          cleanFilters[key] === '' ||
          cleanFilters[key] === null ||
          cleanFilters[key] === undefined
        ) {
          delete cleanFilters[key];
        }
      });

      if (cleanFilters.group === 'todos' || cleanFilters.group === 'all') {
        delete cleanFilters.group;
      }

      const response = await apiClient.get('subscribers/coupons', {
        params: cleanFilters,
      });
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    ...options,
  });
};

export const useSendMails = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (emails) => {
      const response = await apiClient.post('mailing/send-coupons', emails);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subscriberKeys.mailing() });
    },
    onError: (error) => {
      console.error('Error sending mails:', error);
    },
  });
};

export const useSendDebtMails = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (emails) => {
      const response = await apiClient.post('mailing/send-debt', emails);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subscriberKeys.mailing() });
    },
    onError: (error) => {
      console.error('Error sending debt mails:', error);
    },
  });
};
